// 레이드 지휘소 창·레이드 HUD·결과 화면이 쓰는 표시 값(순수 함수, DOM 없음). 값은 실제 규칙(content/raid·items·economy)에서 바로 만든다.
import { MERCS, PLAYABLE_RAIDS, raidDef, raidHardDef, WYVERN, type RaidDiff, type RaidId } from '../shared/content/raid';
import { SETS } from '../shared/content/items';
import { STONE_TIERS, stoneTierForIlvl } from '../shared/content/economy';
import { WEEK_EPOCH_MS, WEEK_MS } from '../shared/content/loot';
import { PROTECT_AS_STONES } from '../shared/content/raidReward';
import { PARTY_CODE_RE } from '../shared/partyNet';
import type { RaidRoomView } from '../shared/raidNet';

const num = (v: number): string => v.toLocaleString('ko-KR');

export interface RaidRewardLine { readonly icon: 'gold' | 'set' | 'legend' | 'stone' | 'protect'; readonly text: string; readonly note?: string }

export interface RaidPanelModel {
  readonly raid: RaidId;
  readonly name: string;
  readonly recLevel: number;
  readonly heroLevel: number;
  /** 권장 레벨보다 낮다(입장은 막지 않는다) */
  readonly underLevel: boolean;
  readonly clears: number;
  readonly limit: number;
  readonly practice: boolean;
  /** "보상 2/3 남음" 같은 한 줄 */
  readonly weekLine: string;
  /** 다음 월요일 00:00 KST까지 */
  readonly resetLine: string;
  readonly rewards: readonly RaidRewardLine[];
  readonly patterns: readonly string[];
  readonly party: readonly { readonly role: string; readonly name: string; readonly title: string; readonly line: string }[];
  readonly enterLabel: string;
  /** 입장할 수 없는 이유('' = 들어갈 수 있다) */
  readonly blocked: string;
  /** 난이도 탭(C13): 지금 보는 난이도·하드 해금·하드 첫 클리어를 받았는가·하드 첫 클리어 줄·광폭 문구·칭호 */
  readonly diff: RaidDiff;
  readonly hardOpen: boolean;
  readonly hardFirst: boolean;
  readonly hardLockNote: string;
  readonly enrageLine: string;
  readonly firstLine: string;
  readonly title: string;
}

/** 다음 주 경계까지 남은 시간 문구 */
export function raidResetText(nowMs: number): string {
  const w = Math.floor((nowMs - WEEK_EPOCH_MS) / WEEK_MS);
  const left = Math.max(0, WEEK_EPOCH_MS + (w + 1) * WEEK_MS - nowMs);
  const d = Math.floor(left / 86400000), h = Math.floor((left % 86400000) / 3600000), m = Math.floor((left % 3600000) / 60000);
  return '초기화까지 ' + (d > 0 ? d + '일 ' + h + '시간' : h > 0 ? h + '시간 ' + m + '분' : Math.max(1, m) + '분') + ' (월요일 00:00)';
}

const MERC_LINE: Record<string, string> = {
  tank: '비룡의 시선을 끌고 주기적으로 도발해요',
  archer: '멀리서 꾸준히 화살을 쏴요',
  priest: '가장 다친 파티원을 치유해요',
};

export interface RaidPanelInput {
  readonly heroLevel: number;
  readonly clears: number;
  readonly limit: number;
  readonly town: number;
  readonly highestTown: number;
  /** 보호 주문서 해금(지역6 클리어) */
  readonly protectOk: boolean;
  readonly locked: boolean;
  readonly nowMs: number;
  /** 난이도 탭 선택(없으면 노말). 하드가 잠겼거나 없는 레이드면 노말로 돌린다 */
  readonly diff?: RaidDiff;
  readonly hardOpen?: boolean;
  readonly hardFirst?: boolean;
}

const ENRAGE_NORMAL = '바닥 경고가 차오르면 밖으로 피하세요. 남는 불 장판은 밟을 때마다 데미지를 입어요. 6분이 지나면 광폭해요.';
const ENRAGE_HARD = '바닥 경고가 차오르면 밖으로 피하세요. 남는 불 장판은 밟을 때마다 데미지를 입어요. 7분이 지나면 광폭해요.';

export function raidPanelModel(i: RaidPanelInput, raid: RaidId = PLAYABLE_RAIDS[0]): RaidPanelModel {
  const hd = raidHardDef(raid), hardOpen = !!hd && i.hardOpen === true;
  const diff: RaidDiff = i.diff === 'hard' && hd && hardOpen ? 'hard' : 'normal';
  const hardFirst = !!hd && i.hardFirst === true;
  const base = raidPanelNormal(i, raid);
  const common = {
    hardOpen, hardFirst, hardLockNote: hd && !hardOpen ? '1차 환생 뒤 열려요' : '', title: hardFirst && hd ? '칭호 「' + hd.first.title + '」' : '',
  };
  if (diff === 'normal' || !hd) return { ...base, diff: 'normal', enrageLine: ENRAGE_NORMAL, firstLine: '', ...common };
  const def = raidDef(raid), loot = def.loot, clears = base.clears;
  const setName = SETS.find((s) => s.id === loot.sets[0])?.name ?? '레이드 세트';
  const tier = stoneTierForIlvl(hd.recLevel), stoneName = STONE_TIERS.find((s) => s.tier === tier)?.name ?? '강화석';
  const firstLeft = clears === 0;
  const rewards: RaidRewardLine[] = [
    { icon: 'gold', text: num(Math.round(loot.gold * hd.loot.goldMult)) + ' 골드', note: '노말의 ×' + hd.loot.goldMult },
    { icon: 'set', text: setName + ' 세트 부위 1개', note: '아이템 레벨 ' + hd.loot.ilvl },
    { icon: 'stone', text: stoneName + ' ' + Math.round(loot.stones * hd.loot.stonesMult) + (firstLeft && !i.protectOk ? ' (+' + PROTECT_AS_STONES + ')' : ''), note: '노말의 ×' + hd.loot.stonesMult },
    { icon: 'legend', text: '전설 장비', note: Math.round(hd.loot.legendChance * 100) + '% 확률' },
  ];
  if (loot.protectFirstClear > 0) rewards.push(base.rewards.find((r) => r.icon === 'protect')!);
  const patterns = [...base.patterns, '잿불 낙인 — 따라다니는 원, 동료와 떨어져 터뜨리세요'];
  const firstLine = '하드 첫 클리어 · 확정 전설 1 · ' + num(hd.first.gold) + ' 골드 · 강화석 ' + hd.first.stones + ' · 칭호 「' + hd.first.title + '」' + (hardFirst ? ' (받음)' : '');
  return {
    ...base, diff: 'hard', recLevel: hd.recLevel, underLevel: i.heroLevel < hd.recLevel, rewards, patterns, enrageLine: ENRAGE_HARD, firstLine, ...common,
  };
}

type PanelBase = Omit<RaidPanelModel, 'diff' | 'hardOpen' | 'hardFirst' | 'hardLockNote' | 'enrageLine' | 'firstLine' | 'title'>;
function raidPanelNormal(i: RaidPanelInput, raid: RaidId): PanelBase {
  const def = raidDef(raid), loot = def.loot;
  const clears = Math.max(0, Math.min(i.limit, i.clears)), practice = clears >= i.limit;
  const setName = SETS.find((s) => s.id === loot.sets[0])?.name ?? '레이드 세트';
  const tier = stoneTierForIlvl(def.recLevel), stoneName = STONE_TIERS.find((s) => s.tier === tier)?.name ?? '강화석';
  const firstLeft = clears === 0;
  const rewards: RaidRewardLine[] = [
    { icon: 'gold', text: num(loot.gold) + ' 골드', note: '장비 골드 옵션이 붙어요' },
    { icon: 'set', text: setName + ' 세트 부위 1개', note: '투구·갑옷·장갑·장화 중 무작위' },
    { icon: 'stone', text: stoneName + ' ' + loot.stones + (firstLeft && !i.protectOk ? ' (+' + PROTECT_AS_STONES + ')' : '') },
    { icon: 'legend', text: '전설 장비', note: Math.round(loot.legendChance * 100) + '% 확률' },
  ];
  if (loot.protectFirstClear > 0) rewards.push({ icon: 'protect', text: '보호 주문서 ' + loot.protectFirstClear + '장', note: i.protectOk ? (firstLeft ? '이번 주 첫 클리어' : '이번 주 받음') : '지역 6 클리어 전에는 강화석 +' + PROTECT_AS_STONES + '로 받아요' });
  const unlockTown = 'town' in def.unlock ? def.unlock.town : 0;
  const blocked = i.locked ? '지금은 들어갈 수 없어요' : unlockTown > 0 && i.highestTown < unlockTown ? '마을 ' + unlockTown + '에 도착하면 열려요' : '';
  return {
    raid, name: def.name, recLevel: def.recLevel, heroLevel: i.heroLevel, underLevel: i.heroLevel < def.recLevel,
    clears, limit: i.limit, practice,
    weekLine: practice ? '이번 주 보상을 모두 받았어요 · 연습(경험치만)' : '이번 주 보상 ' + (i.limit - clears) + '/' + i.limit + '회 남음',
    resetLine: raidResetText(i.nowMs),
    rewards,
    patterns: def.phases.flatMap((p, k) => p.patterns.map((t) => (k === 0 ? '' : '체력 ' + Math.round(p.hpFrom * 100) + '% 이하 · ') + t)),
    party: MERCS.map((m) => ({ role: m.role, name: m.name, title: m.title, line: MERC_LINE[m.role] ?? '' })),
    enterLabel: practice ? '연습 입장' : '출정',
    blocked,
  };
}

/** HUD 광폭 시계: 남은 틱 → "광폭까지 4:12", 광폭이면 "광폭!" */
export function enrageText(enraged: boolean, ticks: number): string {
  if (enraged) return '광폭!';
  const s = Math.max(0, Math.ceil(ticks / 60));
  return '광폭까지 ' + Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

/** HUD 페이즈 띠 문구 */
export function phaseText(phase: number, air: number): string {
  if (air > 0) return '비행 중 — 브레스 줄을 피하세요';
  return phase >= 2 ? '2페이즈 · 곧 날아올라요' : '1페이즈';
}

/** 광폭 문턱(틱): 입장 뒤 이만큼 지나면 광폭 */
export const ENRAGE_TICKS = WYVERN.enrage.at;

export interface RaidResultInput {
  readonly raid?: string;
  readonly raidN?: number;
  readonly raidLimit?: number;
  readonly raidSet?: string;
  readonly raidLegend?: string;
  readonly raidStones?: number;
  readonly raidStoneTier?: number;
  readonly raidProtect?: number;
  /** 넷 판 보상 안내(보상이 우편함으로 간다, 없으면 '') */
  readonly raidNote?: string;
  /** 하드(C13): 난이도·첫 클리어 보상 */
  readonly raidDiff?: string;
  readonly raidFirst?: boolean;
  readonly raidFirstGold?: number;
  readonly raidFirstLegend?: string;
  readonly raidFirstStones?: number;
  readonly gold: number;
}

/** 레이드 결과 화면 문구: 제목·보상 줄·장비 줄(연습이면 장비 줄 빈 칸) */
export function raidResultLines(r: RaidResultInput, equipName: (id: string) => string): { title: string; reward: string; items: string; diffTag: string; firstBand: string; firstItems: string } {
  const name = r.raid && PLAYABLE_RAIDS.includes(r.raid as RaidId) ? raidDef(r.raid as RaidId).name : '레이드';
  const n = r.raidN ?? 0, limit = r.raidLimit ?? 3;
  const hard = r.raidDiff === 'hard', hd = hard && r.raid && PLAYABLE_RAIDS.includes(r.raid as RaidId) ? raidHardDef(r.raid as RaidId) : null;
  const diffTag = hard ? '하드' : '';
  // 하드 첫 클리어 띠: 칭호·확정 전설 이름·골드·강화석(주 보상과 따로 받는다 — 연습 판이어도 나온다)
  const firstOn = hard && r.raidFirst === true;
  const firstBand = firstOn ? '하드 첫 클리어!' + (hd ? ' 칭호 「' + hd.first.title + '」' : '') : '';
  const firstParts: string[] = [];
  if (firstOn) {
    if (r.raidFirstLegend) firstParts.push('★ ' + equipName(r.raidFirstLegend));
    if ((r.raidFirstGold ?? 0) > 0) firstParts.push(num(r.raidFirstGold ?? 0) + ' 골드');
    if ((r.raidFirstStones ?? 0) > 0) firstParts.push('강화석 ' + r.raidFirstStones);
  }
  const firstItems = firstParts.length ? '첫 클리어 보상 · ' + firstParts.join(' · ') : '';
  if (n <= 0) return { title: name + ' 토벌', reward: r.raidNote ? r.raidNote : '연습 판 · 이번 주 보상은 모두 받았어요(경험치만)', items: '', diffTag, firstBand, firstItems };
  const parts: string[] = [];
  if (r.raidSet) parts.push(equipName(r.raidSet));
  if (r.raidLegend) parts.push('★ ' + equipName(r.raidLegend));
  if ((r.raidStones ?? 0) > 0) parts.push((STONE_TIERS.find((s) => s.tier === r.raidStoneTier)?.name ?? '강화석') + ' ' + r.raidStones);
  if ((r.raidProtect ?? 0) > 0) parts.push('보호 주문서 ' + r.raidProtect);
  return { title: name + ' 토벌', reward: '이번 주 보상 ' + n + '/' + limit + ' · ' + num(r.gold) + ' 골드', items: '획득 · ' + parts.join(' · ') + (r.raidNote ? ' · 우편함으로 보냈어요' : ''), diffTag, firstBand, firstItems };
}

// ── 같이 출정(4인) 로비 표시 값 ─────────────────────────────────────────


const HERO_LABEL: Readonly<Record<string, string>> = { kael: '카엘', lyra: '라이라', seren: '세렌', brann: '브란', lumen: '루멘' };

/** 서버 오류 코드 → 한국어 한 줄 */
export function raidNetErrorText(code: string): string {
  switch (code) {
    case '': return '';
    case 'full': return '방이 가득 찼어요';
    case 'no-raid': return '없는 방이에요 · 코드를 확인하세요';
    case 'in-run': return '이미 출발한 방이에요';
    case 'not-ready': return '아직 준비하지 않은 파티원이 있어요';
    case 'not-leader': return '출발은 방장만 할 수 있어요';
    case 'version': return '게임을 새로고침해서 최신 버전으로 들어오세요';
    case 'replaced': return '다른 곳에서 접속해 연결이 끊겼어요';
    case 'kicked': return '연결이 너무 불안정해 방에서 나왔어요';
    case 'session': case 'identity': case 'no-session': return '로그인 정보를 확인하지 못했어요';
    case 'raid-busy': return '방이 많아요 · 잠시 뒤 다시 해 보세요';
    case 'run-expired': return '자리 유지 시간이 지나 판에서 빠졌어요'
    case 'party-active': return '파티 판 중에는 레이드에 들어갈 수 없어요'
    case 'raid-disabled': return '레이드 서버가 아직 열리지 않았어요';
    case 'join-failed': return '방에 들어가지 못했어요 · 코드를 확인하세요';
    case 'connect': return '서버에 연결하지 못했어요';
    default: return '문제가 생겼어요 (' + code + ')';
  }
}

export interface RaidNetSlotLine {
  readonly slot: number;
  readonly kind: 'human' | 'merc' | 'empty';
  /** 사람: 이름, 용병: 이름 없이 「용병: 방패병」 */
  readonly title: string;
  /** 사람: 「카엘 Lv25」, 용병: 역할 한 줄 */
  readonly sub: string;
  readonly ready: boolean;
  readonly online: boolean;
  readonly leader: boolean;
  readonly me: boolean;
  readonly role: string;
}
export interface RaidNetPanelModel {
  /** idle 방 없음 · connecting · retrying · lobby 대기 · count 곧 출발 · run 판 중 · closed 끊김 */
  readonly mode: 'idle' | 'connecting' | 'retrying' | 'lobby' | 'count' | 'run' | 'closed';
  readonly code: string;
  readonly slots: readonly RaidNetSlotLine[];
  readonly humans: number;
  /** 자동 충원 출발까지 남은 초(-1 = 아직 사람이 없거나 로비가 아님) */
  readonly fillLeft: number;
  readonly fillLine: string;
  readonly meReady: boolean;
  readonly amLeader: boolean;
  /** 방장이 「출발」을 누를 수 있는가(나를 뺀 접속한 사람이 모두 준비) */
  readonly canStart: boolean;
  readonly canReady: boolean;
  readonly error: string;
  readonly weekLeft: number;
}

export interface RaidNetPanelInput {
  readonly status: 'idle' | 'connecting' | 'online' | 'retrying' | 'closed';
  readonly room: RaidRoomView | null;
  readonly you: number;
  readonly error: string;
  /** 서버 시각(ms, Date.now) — 자동 충원 카운트다운 */
  readonly nowMs: number;
  /** 내 영웅 이름(방에 아직 못 들어갔을 때 표시) */
  readonly heroName?: string;
}

export function validRaidCode(raw: string): boolean { return PARTY_CODE_RE.test(raw.trim().toUpperCase()); }

export function raidNetPanelModel(i: RaidNetPanelInput): RaidNetPanelModel {
  const room = i.room;
  const mode: RaidNetPanelModel['mode'] = i.status === 'closed' ? 'closed' : i.status === 'connecting' ? 'connecting' : i.status === 'retrying' ? 'retrying'
    : !room ? (i.status === 'online' ? 'connecting' : 'idle')
    : room.phase === 'run' ? 'run' : room.phase === 'count' ? 'count' : room.phase === 'end' ? 'lobby' : 'lobby';
  const slots: RaidNetSlotLine[] = [];
  let humans = 0, meReady = false, amLeader = false;
  for (let s = 0; s < 4; s++) {
    const mem = room ? room.members.find((m) => m.slot === s) : undefined;
    if (mem) {
      humans++;
      const me = s === i.you;
      if (me) { meReady = mem.ready; amLeader = mem.leader; }
      slots.push({ slot: s, kind: 'human', title: mem.name + (me ? ' (나)' : ''), sub: (HERO_LABEL[mem.hero] ?? '모험가') + ' Lv' + mem.lv + (mem.online ? '' : ' · 끊김'), ready: mem.ready, online: mem.online, leader: mem.leader, me, role: 'hero' });
      continue;
    }
    const mi = room ? room.mercs[s] : -1;
    const def = mi !== undefined && mi >= 0 ? MERCS[mi] : undefined;
    if (def) slots.push({ slot: s, kind: 'merc', title: '용병: ' + def.title, sub: def.name + ' · ' + (MERC_LINE[def.role] ?? ''), ready: true, online: true, leader: false, me: false, role: def.role });
    else slots.push({ slot: s, kind: 'empty', title: '빈자리', sub: '', ready: false, online: false, leader: false, me: false, role: '' });
  }
  const fillAt = room && room.phase === 'lobby' ? room.fillAt : 0;
  const fillLeft = fillAt > 0 ? Math.max(0, Math.ceil((fillAt - i.nowMs) / 1000)) : -1;
  const others = room ? room.members.filter((m) => m.slot !== i.you && m.online) : [];
  const canStart = mode === 'lobby' && amLeader && others.every((m) => m.ready);
  const fillLine = fillLeft < 0 ? '' : fillLeft === 0 ? '모두 준비하면 바로 출발 · 빈자리는 용병이 채워요' : fillLeft + '초 뒤 모두 준비하면 자동 출발 · 빈자리는 용병이 채워요';
  return {
    mode, code: room ? room.code : '', slots, humans, fillLeft, fillLine, meReady, amLeader, canStart, canReady: mode === 'lobby' && !!room,
    error: raidNetErrorText(i.error), weekLeft: room ? room.weekLeft : -1,
  };
}

/** 나가기 띠: 한 번 누르면 3초 동안 「정말 나가기?」, 그 안에 한 번 더 누르면 나간다(페이지 confirm 대신) */
export const RAID_LEAVE_ARM_MS = 3000;
export function raidLeaveStep(nowMs: number, armedAt: number): { armed: boolean; leave: boolean } {
  if (armedAt > 0 && nowMs - armedAt <= RAID_LEAVE_ARM_MS) return { armed: false, leave: true };
  return { armed: true, leave: false };
}
